/**
 * Onboarding Checklist Types
 * Onboarding checklist for new users with 3 steps
 */

/**
 * Onboarding step identifiers
 */
export type OnboardingStepId = 'start_project' | 'edit_project' | 'publish_share';

/**
 * Onboarding progress stored in users.context (JSONB)
 */
export interface OnboardingProgress {
  /** ISO timestamp - Step 1 durable marker (set once when first project created) */
  firstProjectCreatedAt?: string;

  /** ISO timestamp - Step 2 durable marker (set once when first edit made via agent/visual editor/file save) */
  firstEditMadeAt?: string;

  /** ISO timestamp - Step 3 durable marker (set once when first successful publish deploy) */
  firstPublishedAt?: string;

  /** ISO timestamp - User clicked X to hide forever */
  dismissedAt?: string;

  /** ISO timestamp - All 3 steps completed */
  completedAt?: string;
}

/**
 * Prerequisites computed on server for step enable/disable logic
 */
export interface OnboardingPrerequisites {
  /** Step 1 completed: user has ever created a project */
  step1Completed: boolean;

  /** Step 2 completed: user has made an edit (via agent, visual editor, or file save) */
  step2Completed: boolean;

  /** Step 3 completed: user has successfully published a deploy */
  step3Completed: boolean;
}

/**
 * API Response for GET /api/users/me/onboarding
 */
export interface OnboardingResponse {
  /** Display control - false if not new user or dismissed */
  showChecklist: boolean;

  /** Completed step IDs (server-computed) */
  completedSteps: OnboardingStepId[];

  /** First incomplete + enabled step (for highlighting) */
  recommendedStep: OnboardingStepId | null;

  /** Prerequisites for step enable/disable logic (progressive unlock) */
  prerequisites: OnboardingPrerequisites;

  /** Most recently updated project ID for CTA deep-linking */
  lastActiveProjectId: string | null;

  /** ISO timestamp - User clicked X to dismiss */
  dismissedAt: string | null;

  /** ISO timestamp - All steps completed */
  completedAt: string | null;
}

/**
 * API Response for mark step endpoints (mark-first-project, mark-first-edit, mark-first-publish).
 * Unified response for all idempotent mark endpoints.
 */
export interface MarkStepResponse {
  /** true = newly marked, false = already marked */
  marked: boolean;
  /** ISO timestamp of when the marker was set (null if not marked) */
  timestamp: string | null;
}

/**
 * Default response when user is not eligible for onboarding
 */
export const DEFAULT_ONBOARDING_RESPONSE: OnboardingResponse = {
  showChecklist: false,
  completedSteps: [],
  recommendedStep: null,
  prerequisites: {
    step1Completed: false,
    step2Completed: false,
    step3Completed: false,
  },
  lastActiveProjectId: null,
  dismissedAt: null,
  completedAt: null,
};
